import type { UsageTimelineLedger, UsageTimelineDurationEvidenceRecord } from '../types/admin';

const normalized = new WeakMap<object, {
  table: object; entries: object; actors: object; start: string; end: string;
  generation: string; scope: string; dependency: string;
}>();
const canonicalWorkflows = new WeakSet<object>();
export function hasCanonicalTimelineDurationEvidence(workflow: object): boolean {
  return canonicalWorkflows.has(workflow);
}

function validDay(day: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(Date.parse(`${day}T00:00:00Z`))
    && new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) === day;
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Validate the canonical wire table once, then share immutable proof maps.
 * Nonenumerable hydration preserves the original compact JSON representation:
 * caches cannot undercount a repeated enumerable map after normalization.
 */
export function normalizeUsageTimelineDurationEvidence(ledger: UsageTimelineLedger): boolean {
  if (!ledger || !Array.isArray(ledger.entries)) return false;
  const table = ledger.duration_evidence;
  if (table === undefined) return ledger.entries.every(entry => entry?.workflow?.duration_evidence_ref === undefined);
  const prior = normalized.get(ledger);
  if (prior) return table === prior.table && ledger.entries === prior.entries && ledger.actors === prior.actors
    && ledger.coverage?.start === prior.start && ledger.coverage?.end === prior.end
    && ledger.coverage?.generation_revision === prior.generation && ledger.coverage?.scope_revision === prior.scope
    && ledger.coverage?.dependency_revision === prior.dependency;
  if (!object(table) || table.version !== 'duration-evidence-v1' || !ledger.coverage
    || !Array.isArray(ledger.actors) || !Array.isArray(table.records) || table.records.length > ledger.entries.length
    || !validDay(ledger.coverage.start) || !validDay(ledger.coverage.end)
    || table.start !== ledger.coverage.start || table.end !== ledger.coverage.end
    || table.generation_revision !== ledger.coverage.generation_revision
    || table.scope_revision !== ledger.coverage.scope_revision
    || table.dependency_revision !== ledger.coverage.dependency_revision) return false;
  const authorized = new Set(ledger.actors.map(actor => actor.email));
  const records = new Map<string, { record: UsageTimelineDurationEvidenceRecord; users: Set<string> }>();
  const flowIds = new Set<string>();
  for (const record of table.records) {
    if (!object(record) || typeof record.id !== 'string' || !record.id || record.id.length > 512
      || records.has(record.id) || typeof record.flow_id !== 'string' || record.flow_id.length > 512
      || (record.source_flow_id !== undefined && typeof record.source_flow_id !== 'string')
      || flowIds.has(record.flow_id) || !Array.isArray(record.users) || record.users.length > 512
      || record.users.some(email => typeof email !== 'string' || !authorized.has(email))
      || (record.active_ms !== undefined && record.active_ms !== null
        && (typeof record.active_ms !== 'number' || !Number.isFinite(record.active_ms) || record.active_ms < 0))) return false;
    const users = new Set(record.users);
    if (users.size !== record.users.length) return false;
    let total = 0;
    const proof = record.active_ms_by_day_and_user;
    if (proof !== undefined) {
      if (!object(proof) || Object.keys(proof).length > 31) return false;
      for (const [day, actors] of Object.entries(proof)) {
        if (!validDay(day) || day < ledger.coverage.start || day > ledger.coverage.end
          || !object(actors) || Object.keys(actors).length > 512) return false;
        for (const [email, duration] of Object.entries(actors)) {
          if (!users.has(email) || typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0) return false;
          total += duration;
        }
      }
      if (!Number.isFinite(total) || total !== record.active_ms) return false;
    }
    const lastActive = record.user_last_active;
    if (lastActive !== undefined) {
      if (!object(lastActive)) return false;
      for (const [email, timestamp] of Object.entries(lastActive)) {
        if (!users.has(email) || typeof timestamp !== 'string' || !timestamp.endsWith('Z')
          || !Number.isFinite(Date.parse(timestamp)) || timestamp.slice(0, 10) < ledger.coverage.start
          || timestamp.slice(0, 10) > ledger.coverage.end) return false;
      }
    }
    if (proof === undefined && lastActive === undefined) return false;
    records.set(record.id, { record, users }); flowIds.add(record.flow_id);
  }
  const referenced = new Set<string>();
  for (const entry of ledger.entries) {
    const workflow = entry?.workflow;
    if (!object(workflow) || typeof workflow.flow_id !== 'string') return false;
    const ref = workflow.duration_evidence_ref;
    if (ref === undefined) {
      if (flowIds.has(workflow.flow_id)) return false;
      continue;
    }
    if (typeof ref !== 'string') return false;
    const canonical = records.get(ref);
    if (!canonical || workflow.flow_id !== canonical.record.flow_id
      || workflow.source_flow_id !== canonical.record.source_flow_id
      || workflow.active_ms !== canonical.record.active_ms || !Array.isArray(workflow.users)
      || workflow.users.length !== canonical.users.size || new Set(workflow.users).size !== workflow.users.length
      || workflow.users.some(email => !canonical.users.has(email))
      || Object.prototype.hasOwnProperty.call(workflow, 'active_ms_by_day_and_user')
      || Object.prototype.hasOwnProperty.call(workflow, 'user_last_active')) return false;
    referenced.add(ref);
  }
  if (referenced.size !== records.size) return false;
  // No mutation until every record/reference has passed validation.
  for (const { record } of Array.from(records.values())) {
    if (record.active_ms_by_day_and_user) {
      Object.values(record.active_ms_by_day_and_user).forEach(Object.freeze);
      Object.freeze(record.active_ms_by_day_and_user);
    }
    if (record.user_last_active) Object.freeze(record.user_last_active);
    Object.freeze(record.users); Object.freeze(record);
  }
  for (const entry of ledger.entries) {
    if (entry.workflow.duration_evidence_ref === undefined) continue;
    const record = records.get(entry.workflow.duration_evidence_ref)!.record;
    for (const field of ['active_ms_by_day_and_user', 'user_last_active'] as const) {
      if (record[field] !== undefined) Object.defineProperty(entry.workflow, field, { value: record[field], enumerable: false });
    }
    Object.freeze(entry.workflow.users); Object.freeze(entry.workflow);
    canonicalWorkflows.add(entry.workflow);
  }
  Object.freeze(table.records); Object.freeze(table);
  ledger.entries.forEach(Object.freeze); Object.freeze(ledger.entries);
  ledger.actors.forEach(Object.freeze); Object.freeze(ledger.actors);
  normalized.set(ledger, { table, entries: ledger.entries, actors: ledger.actors,
    start: ledger.coverage.start, end: ledger.coverage.end, generation: ledger.coverage.generation_revision,
    scope: ledger.coverage.scope_revision, dependency: ledger.coverage.dependency_revision });
  return true;
}
